import React from 'react'

import MultiSelect from '../../ui/MultiSelect'
import SingleSelect from '../../ui/SingleSelect'
import { useCatalog } from '../../../contexts/CatalogContext'
import type { CatalogCategoryKey } from '../../../catalog/categoryKeys'

/**
 * Controlled multi-select chip editor backed by a single catalog category, with
 * inline "add to catalog" support.
 *
 * Used by `WpsBaseMaterial` for Material Spec, Type/Grade, Pipe Manufacturer,
 * Slab Source, Plate Source. Decoupled from any form context so the same cell
 * can later be embedded inside other editors (e.g. PQR preview).
 */
export interface BaseMaterialCatalogChipsProps {
  values: string[]
  categoryKey: CatalogCategoryKey
  placeholder: string
  addLabel: string
  className?: string
  interactionLocked?: boolean
  lockedHint?: string
  onChange: (values: string[]) => void
}

export function BaseMaterialCatalogChips({
  values,
  categoryKey,
  placeholder,
  addLabel,
  className,
  interactionLocked,
  lockedHint,
  onChange
}: BaseMaterialCatalogChipsProps) {
  const { getLabelsMerged, addLabel: addCatalogLabel } = useCatalog()
  const options = getLabelsMerged(categoryKey, values)

  return (
    <MultiSelect
      values={values}
      options={options}
      placeholder={placeholder}
      addLabel={addLabel}
      interactionLocked={interactionLocked}
      lockedHint={lockedHint}
      onChange={onChange}
      onCreateOption={async (value) => {
        await addCatalogLabel(categoryKey, value)
        if (!values.includes(value)) onChange([...values, value])
      }}
      className={className}
    />
  )
}

/**
 * Controlled single-select chip editor backed by a single catalog category,
 * with inline "add to catalog" support.
 *
 * Used by `WpsBaseMaterial` for per-pipe Heat Number, and by the PQR preview
 * for Material Spec / Type/Grade / Pipe Manufacturer / Heat No. (where each
 * row is a single value rather than a chip set).
 */
export interface BaseMaterialCatalogPickerProps {
  value: string
  categoryKey: CatalogCategoryKey
  placeholder: string
  addLabel: string
  className?: string
  interactionLocked?: boolean
  lockedHint?: string
  /** If set, `addLabel` is created scoped to this project (e.g. heat number). */
  scopedProjectId?: string
  onChange: (value: string) => void
}

export function BaseMaterialCatalogPicker({
  value,
  categoryKey,
  placeholder,
  addLabel,
  className,
  interactionLocked,
  lockedHint,
  scopedProjectId,
  onChange
}: BaseMaterialCatalogPickerProps) {
  const { getLabelsMerged, addLabel: addCatalogLabel } = useCatalog()
  const options = getLabelsMerged(categoryKey, value ? [value] : [])

  return (
    <SingleSelect
      value={value}
      options={options}
      placeholder={placeholder}
      addLabel={addLabel}
      className={className}
      interactionLocked={interactionLocked}
      lockedHint={lockedHint}
      getTriggerLabel={(option) => option}
      onChange={onChange}
      onCreateOption={async (raw) => {
        const trimmed = raw.trim()
        if (!trimmed) return ''
        await addCatalogLabel(categoryKey, trimmed, scopedProjectId)
        onChange(trimmed)
        return trimmed
      }}
    />
  )
}
